import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { userAPI, authAPI } from '../services/api';

const AuthContext = createContext();

// Session timeout: 1 hour of inactivity (in milliseconds)
const SESSION_TIMEOUT = 60 * 60 * 1000; // 1 hour
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Logout function - defined first so it can be used by other functions
  const logout = useCallback(async (silent = false) => {
    // Clear timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      // Call backend logout endpoint (only if not silent logout)
      if (!silent && token) {
        await authAPI.logout();
      }
    } catch (error) {
      // Continue with logout even if backend call fails
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local state
      localStorage.removeItem('token');
      localStorage.removeItem('lastActivity');
      setToken(null);
      setUser(null);
    }
  }, [token]);

  // Reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    if (!token) return;

    // Update last activity timestamp
    lastActivityRef.current = Date.now();
    localStorage.setItem('lastActivity', lastActivityRef.current.toString());

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      console.log('Session expired due to inactivity');
      logout(true);
      // Show alert after state update
      setTimeout(() => {
        alert('Your session has expired due to inactivity. Please log in again.');
      }, 100);
    }, SESSION_TIMEOUT);
  }, [token, logout]);

  // Check if session has expired on page load/refresh
  const checkSessionExpiry = useCallback(() => {
    const lastActivity = localStorage.getItem('lastActivity');
    if (lastActivity) {
      const timeSinceActivity = Date.now() - parseInt(lastActivity, 10);
      if (timeSinceActivity >= SESSION_TIMEOUT) {
        console.log('Session expired - logging out');
        logout(true);
        return true;
      }
    }
    return false;
  }, [logout]);

  // Fetch user function
  const fetchUser = useCallback(async () => {
    try {
      const response = await userAPI.getMe();
      setUser(response.data);
      // Reset activity timer on successful fetch
      resetInactivityTimer();
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout(true);
    } finally {
      setLoading(false);
    }
  }, [resetInactivityTimer, logout]);

  // Setup activity listeners
  useEffect(() => {
    if (!token) return;

    // Check if session already expired
    if (checkSessionExpiry()) {
      return;
    }

    // Start the inactivity timer
    resetInactivityTimer();

    // Add event listeners for user activity
    const handleActivity = () => {
      resetInactivityTimer();
    };

    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      ACTIVITY_EVENTS.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [token, resetInactivityTimer, checkSessionExpiry]);

  // Fetch user on mount if token exists
  useEffect(() => {
    if (token) {
      // Check session expiry first
      if (!checkSessionExpiry()) {
        fetchUser();
      }
    } else {
      setLoading(false);
    }
  }, [token, checkSessionExpiry, fetchUser]);

  const login = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('lastActivity', Date.now().toString());
    setToken(newToken);
    setUser(userData);
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  // Alias for refreshing user data (e.g., after subscription changes)
  const refreshUser = fetchUser;

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout, updateUser, fetchUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};